import React, { FC, useContext, useEffect } from 'react';
import Routes from 'Routes';
import { Route } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectLocale } from 'models/localization';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import styled from 'styled-components';
import AppBar from 'components/appbar/XtraAppBar';
import { EventDatetimeContext } from 'components/EventDatetime';
import { useMediaQuery } from '@material-ui/core';
import DinCondensedFontOTF from 'assets/fonts/D-DIN.otf';
import DinCondensedFontEOT from 'assets/fonts/D-DIN.eot';
import FloatingFooter from 'components/footer_v2/FloatingFooter';
import 'objectFitPolyfill';

import BackgroundVideo from './BackgroundVideo';
import EventEndContent from './EventEndContent';
import LandingContent from './LandingContent';

interface PageContainerProps {
  backgroundVideoSrc: string;
  backgroundImgSrc: string;
  isDesktop: boolean;
  fullWidth: boolean;
}

const PageContainer = styled.div<PageContainerProps>`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: auto;
  ${({
    isDesktop,
    backgroundImgSrc,
    backgroundVideoSrc,
    fullWidth,
  }) => (
    isDesktop && !backgroundVideoSrc && `
    background: url(${backgroundImgSrc}) no-repeat top center fixed;
    background-size: ${(fullWidth ? 'cover' : 'auto calc(100% - 56px)')};
    background-position-y: 56px;
    `
  )}
`;

const PageHeader = styled.div`
  flex: 0 0 auto;
`;

const BackgroundMobileImage = styled.img`
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  object-fit: cover;
  width: 100%;
  margin: auto;
  z-index: -100;
  height: calc(100vh - calc(100vh - 100%));
`;

const MainFrame = styled.div<{ opacity: number | string }>`
  @font-face {
    font-family: din;
    src:
      url(${DinCondensedFontOTF}),
      url(${DinCondensedFontEOT}?#iefix) format('embedded-opentype');
  }

  display: flex;
  flex-direction: column;
  justify-content: start;
  overflow: auto;
  height: 100%;
  background: ${({ opacity }) => `rgba(0, 0, 0, ${opacity})`};
`;

const MainContent = styled.div`
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-top: 25px;
  z-index: 5;
`;

const LandingPage: FC = () => {
  const {
    landing: {
      backgroundVideoSrc,
      backgroundImgSrc,
      mobileBreakpoint,
      backgroundMobileImgSrc,
      fullWidth,
      opacity,
      redirectTo = {},
    },
  } = useSelector((state: any) => state.eventConfigs);

  useEffect(() => {
    window.trackingEventToFirebaseWithoutLogin('Landing', 'Enter', '');
  }, []);

  const locale = useSelector(selectLocale);
  const isDesktop = useMediaQuery(`(min-width: ${mobileBreakpoint || 960}px)`);

  const { hasEventEnded } = useContext(EventDatetimeContext);

  const redirectUrl = findAvailableLocale(redirectTo, locale);
  if (redirectUrl && redirectUrl !== Routes.landing) {
    return (
      <Route
        path="/"
        component={() => {
          window.location.href = redirectUrl;
          return null;
        }}
      />
    );
  }

  return (
    <PageContainer
      isDesktop={isDesktop}
      backgroundVideoSrc={backgroundVideoSrc}
      backgroundImgSrc={backgroundImgSrc}
      fullWidth={fullWidth}
      id="landing-root-container"
    >
      <PageHeader id="landing-header">
        <AppBar hideUser hideDrawer hideSearchBar />
      </PageHeader>
      {isDesktop && backgroundVideoSrc && <BackgroundVideo src={backgroundVideoSrc} id="landing-bg-video" type="video/mp4" />}
      {!isDesktop && <BackgroundMobileImage src={backgroundMobileImgSrc || backgroundImgSrc} id="landing-bg-image-mobile" />}
      <MainFrame opacity={opacity ?? 0.5} id="landing-main-frame">
        <MainContent>
          {!hasEventEnded ? <LandingContent /> : <EventEndContent />}
          <FloatingFooter hashState={Routes.landing} />
        </MainContent>
      </MainFrame>
    </PageContainer>
  );
};

export default LandingPage;
